/**
 * Constantes de pasarela compartidas entre frontend y backend.
 *
 * SSOT para tipos de clave PIX, campos de requisito del operador,
 * y valores por defecto de la UI de pasarela.
 *
 * Consumo:
 *   - ESM:      import { TIPO_CLAVE_PIX } from '@shared/config/pasarela-constants.js'
 *   - CommonJS: const { TIPO_CLAVE_PIX } = require('../../shared/config/pasarela-constants')
 *   - Browser:  window.PasarelaConstants.TIPO_CLAVE_PIX
 */

// =========================================================================
// TIPOS DE CLAVE PIX — Identificadores de destino para pagos PIX Brasil
// =========================================================================

var TIPO_CLAVE_PIX = Object.freeze({
    DOCUMENT: 'document',
    PHONE: 'phone',
    EMAIL: 'email',
    RANDOM_KEY: 'random_key',
});

// =========================================================================
// CAMPOS DE REQUISITO — IDs que el admin configura en requisitos_recarga/retiro.
// El front los lee y muestra los campos correspondientes en el modal.
// El backend valida que estén presentes antes de llamar al provider.
// =========================================================================

var CAMPO_REQUISITO = Object.freeze({
    NOMBRE: 'nombre',
    DOCUMENTO_TITULAR: 'documento_titular',
    EMAIL: 'email',
    TELEFONO: 'telefono',
});

// =========================================================================
// CONSTANTES DE UI — Valores por defecto para la interfaz de pasarela.
// Operadores pueden sobreescribir via config heredada del nodo.
// =========================================================================

var MONTOS_RAPIDOS_RECARGA_DEFAULT = Object.freeze([2000, 5000, 10000, 20000]);

var PASARELA_UI_TIMING = Object.freeze({
    POLLING_INTERVALO_MS: 5000,
    CONFIG_CACHE_TTL_MS: 5 * 60 * 1000,
    REVEAL_FADE_MS: 500,
    REVEAL_FADE_RAPIDO_MS: 400,
    REVEAL_TYPEWRITER_MS: 1000,
    // Cooldown del botón de confirmar tras un error de "espera" — vale para las
    // DOS operaciones de la pasarela (retiro: saque repetido/banco ocupado;
    // recarga: rate limit propio): frena el tapeo en ráfaga sin trabar los 2 min
    // del banco — el jugador puede probar otro monto enseguida. Verificado en
    // prod: el jugador reintenta en ráfagas de 10-15s, así que esto mata la
    // mayoría.
    PASARELA_COOLDOWN_TRAS_ERROR_MS: 12000,
    // Techo del cooldown cuando la ventana la dicta el backend (`retryAfter` del
    // 429). Hoy la ventana del rate limit es de 1 minuto, así que nunca se toca;
    // existe porque `RATE_LIMIT_WINDOW_MS` es override-able por env
    // (backend/src/config.js:111) y un valor grande apagaría el botón de pago
    // ese tiempo entero. El botón SIEMPRE tiene que volver: es la salida del
    // jugador hacia su dinero (retiro) y su entrada al juego (recarga).
    PASARELA_COOLDOWN_MAXIMO_MS: 60000,
});

// =========================================================================
// REGLAS DE CAMPO PIX — Longitudes máximas por tipo de clave.
// Evita duplicar maxLength en cada formulario.
// =========================================================================

var REGLAS_CAMPO_PIX = Object.freeze({
    CPF_MAX_LENGTH: 11,
    EMAIL_MAX_LENGTH: 77,
    PHONE_MAX_LENGTH: 11,
    RANDOM_KEY_MAX_LENGTH: 36,
    // Mapa tipo_clave → longitud máxima (para validación dinámica)
    MAX_LENGTH_POR_TIPO_CLAVE: Object.freeze({
        [TIPO_CLAVE_PIX.DOCUMENT]: 11,
        [TIPO_CLAVE_PIX.EMAIL]: 77,
        [TIPO_CLAVE_PIX.PHONE]: 11,
        [TIPO_CLAVE_PIX.RANDOM_KEY]: 36,
    }),
});

// =========================================================================
// ESTADOS DE RESULTADO — Strings devueltos al front por el orquestador.
// =========================================================================

var RESULTADO_PASARELA = Object.freeze({
    VERIFICACION_PENDIENTE: 'verificacion_pendiente',
    EXPIRADO: 'expirado',
    CANCELADO: 'cancelado',
    CONFIRMADO: 'confirmado', // Coincide con ESTADO_TRANSACCION.CONFIRMADO del backend
});

// =========================================================================
// CÓDIGOS DE ERROR API — Strings que el backend envía y el front compara.
// =========================================================================

var CODIGO_ERROR_API = Object.freeze({
    TELEFONO_YA_REGISTRADO: 'ALREADY_EXISTS',
    // Gate del registro cuando una promo activa exige documento fiscal
    // (requisitos_json de la promo): sin documento válido no hay alta.
    DOCUMENTO_REQUERIDO: 'DOCUMENTO_REQUERIDO',
    DOCUMENTO_INVALIDO: 'DOCUMENTO_INVALIDO',
});

// =========================================================================
// CÓDIGOS DE ERROR DE RECARGA/RETIRO — SSOT estable compartido back↔front.
//
// El backend los envía en `body.errorCode` (aditivo: el campo `error` con
// texto en español se mantiene para POS/integradores). El front los mapea a
// `t('pasarela.errores.<CODIGO>')` para mostrar el motivo en el idioma del
// jugador (es Perú, pt Brasil). El valor de cada código ES la sub-clave i18n
// en languages.js → pasarela.errores, así que NUNCA renombrar un valor sin
// actualizar las 4 secciones de idioma a la vez.
// =========================================================================

var CODIGO_ERROR_PASARELA = Object.freeze({
    // Comunes recarga + retiro
    PASARELA_NO_CONFIGURADA: 'PASARELA_NO_CONFIGURADA',
    MONTO_INVALIDO: 'MONTO_INVALIDO',
    MONTO_FUERA_DE_RANGO: 'MONTO_FUERA_DE_RANGO',
    MONTO_MENOR_AL_MINIMO: 'MONTO_MENOR_AL_MINIMO', // detalle: minimo (centavos)
    MONTO_EXCEDE_MAXIMO: 'MONTO_EXCEDE_MAXIMO', // detalle: maximo (centavos)
    DATOS_FALTANTES: 'DATOS_FALTANTES', // detalle: camposFaltantes (array)
    IDEMPOTENCY_KEY_REQUERIDA: 'IDEMPOTENCY_KEY_REQUERIDA', // retiro sin clave de idempotencia (cliente viejo o integrador mal hecho)
    RETIRO_CPF_DISTINTO_TITULAR: 'RETIRO_CPF_DISTINTO_TITULAR', // titularidad: el destino debe ser del mismo CPF que depositó
    MONEDA_NO_COINCIDE: 'MONEDA_NO_COINCIDE', // config del operador, no accionable por el jugador
    PROVIDER_NO_DISPONIBLE: 'PROVIDER_NO_DISPONIBLE', // 502/503 — reintentar más tarde
    // Política del operador: la custodia VLT anónima no puede INICIAR una
    // recarga bancaria nueva (config pasarela.politica_vlt_recarga =
    // cuenta_requerida). Solo bloquea recargas nuevas: polling, webhooks,
    // retiros existentes, claim y caja siguen funcionando.
    RECARGA_VLT_REQUIERE_CUENTA: 'RECARGA_VLT_REQUIERE_CUENTA',

    // Solo retiro
    RETIRO_NO_SOPORTADO: 'RETIRO_NO_SOPORTADO',
    RETIRO_LIMITE_DIARIO: 'RETIRO_LIMITE_DIARIO', // detalle: limiteRestante, limiteDiario, retiradoHoy (centavos)
    RETIRO_SALDO_INSUFICIENTE: 'RETIRO_SALDO_INSUFICIENTE',
    RETIRO_DOCUMENTO_REQUERIDO: 'RETIRO_DOCUMENTO_REQUERIDO',
    RETIRO_FONDOS_PROVIDER_INSUFICIENTES: 'RETIRO_FONDOS_PROVIDER_INSUFICIENTES',
    RETIRO_DESTINO_REQUERIDO: 'RETIRO_DESTINO_REQUERIDO',
    RETIRO_DESTINO_INVALIDO: 'RETIRO_DESTINO_INVALIDO',
    RETIRO_METODO_NO_ENCONTRADO: 'RETIRO_METODO_NO_ENCONTRADO',
    RETIRO_COTIZACION_CAMBIO: 'RETIRO_COTIZACION_CAMBIO',

    // Rechazos del provider clasificados por su catálogo de errores (cada
    // provider mapea sus mensajes a estos códigos en clasificarErrorDeRetiro).
    RETIRO_CHAVE_DE_OTRA_PERSONA: 'RETIRO_CHAVE_DE_OTRA_PERSONA', // titularidad: la chave no es del titular declarado
    RETIRO_CHAVE_EN_BLACKLIST: 'RETIRO_CHAVE_EN_BLACKLIST', // receptor bloqueado por el provider
    RETIRO_REINTENTO_MUY_RAPIDO: 'RETIRO_REINTENTO_MUY_RAPIDO', // anti-duplicado del provider (mismo monto en ventana corta)
    RETIRO_CHAVE_INVALIDA: 'RETIRO_CHAVE_INVALIDA', // formato de chave rechazado (teléfono sin +55, CPF mal tipeado)

    // Banco temporal — el pago NO se creó pero el problema es transitorio del
    // banco, NO del jugador: reintentar funciona (verificado en prod: la jugadora
    // con "acesso bloqueado" sacó 97s después). Mensaje tranquilizador, no asustar.
    // Ambiguos (pagoDefinitivamenteNoCreado:false) → VERIFICANDO → el polling resuelve.
    RETIRO_BANCO_NO_RESPONDE: 'RETIRO_BANCO_NO_RESPONDE', // ECONNREFUSED / "conta transacional não pode ser consultada": el banco no pudo consultar la chave ahora. Sugerir reintentar u otra chave
    RETIRO_BANCO_OCUPADO: 'RETIRO_BANCO_OCUPADO', // 429 rate limit / "O seu acesso foi bloqueado": pausa momentánea del banco. Reintentar en segundos

    // Rate limit NUESTRO (no del banco): el plugin global de Fastify corta el
    // martilleo de POSTs antes de llegar al handler, así que el 429 no trae
    // `errorCode` en el body — lo deriva el front desde el status HTTP. Distinto
    // de RETIRO_BANCO_OCUPADO, que es el banco pidiendo pausa. Medido en prod
    // Brasil (2026-08-27 19:14): 8 POST /api/pasarela/retiro en 19s, todos 429.
    RATE_LIMIT: 'RATE_LIMIT',
});

// =========================================================================
// VARIANTES DE RETIRO — Qué flujo de modal mostrar según historial del jugador
// =========================================================================

var VARIANTE_RETIRO = Object.freeze({
    CON_METODO_GUARDADO: 'con_metodo_guardado',
    PRIMERA_VEZ: 'primera_vez',
});

// =========================================================================
// POLÍTICA DE RECARGA BANCARIA EN VLT — ¿puede una custodia VLT anónima
// iniciar una NUEVA recarga bancaria? Valores cerrados, agnósticos del
// provider (no eligen PIX/Yape/preset: eso sigue en el resolver de pasarela).
// Se persiste en config_json.pasarela.politica_vlt_recarga y se hereda
// CAMPO-A-CAMPO (pasarela-manager.resolverPoliticaVltRecarga).
// =========================================================================

var POLITICA_VLT_RECARGA = Object.freeze({
    ANONIMA_PERMITIDA: 'anonima_permitida',
    CUENTA_REQUERIDA: 'cuenta_requerida',
});

// =========================================================================
// EXPORTS
// =========================================================================

var PasarelaConstants = {
    TIPO_CLAVE_PIX: TIPO_CLAVE_PIX,
    CAMPO_REQUISITO: CAMPO_REQUISITO,
    MONTOS_RAPIDOS_RECARGA_DEFAULT: MONTOS_RAPIDOS_RECARGA_DEFAULT,
    PASARELA_UI_TIMING: PASARELA_UI_TIMING,
    REGLAS_CAMPO_PIX: REGLAS_CAMPO_PIX,
    RESULTADO_PASARELA: RESULTADO_PASARELA,
    VARIANTE_RETIRO: VARIANTE_RETIRO,
    CODIGO_ERROR_API: CODIGO_ERROR_API,
    CODIGO_ERROR_PASARELA: CODIGO_ERROR_PASARELA,
    POLITICA_VLT_RECARGA: POLITICA_VLT_RECARGA,
};

// ESM
export {
    TIPO_CLAVE_PIX,
    CAMPO_REQUISITO,
    MONTOS_RAPIDOS_RECARGA_DEFAULT,
    PASARELA_UI_TIMING,
    REGLAS_CAMPO_PIX,
    RESULTADO_PASARELA,
    VARIANTE_RETIRO,
    CODIGO_ERROR_API,
    CODIGO_ERROR_PASARELA,
    POLITICA_VLT_RECARGA,
};
export default PasarelaConstants;

// Browser global
if (typeof window !== 'undefined') {
    window.PasarelaConstants = PasarelaConstants;
}

// CommonJS
if (typeof module !== 'undefined' && module.exports) {
    module.exports = PasarelaConstants;
}
